//src/pages/PatientInQueuse/index.jsx
import {
  Box,
  Button,
  Typography,
  useTheme,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  Divider,
  Chip,
  Alert,
  Stack,
  Tabs,
  Tab,
} from "@mui/material";
import { tokens } from "../../theme";
import Calendar from "../calendar/calendar";
import { useState, useMemo, useRef } from "react";
import React, { useEffect } from "react";
import appointmentService from "../../services/appointmentService"; // Import the service
import { format } from "date-fns";
import Refresh from "@mui/icons-material/Refresh";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import { useLocation, useNavigate } from "react-router-dom";
import {
  PAGE_TABS_BAR_SX,
  PAGE_TAB_LIST_PROPS,
} from "../../utils/pageLayoutStyles";
import { getAll as getAllAdmissions, getAdmissionsByDoctorId } from "../../services/PatientAdmissionService";
import { getPatientById } from "../../services/patientService";
import PatientTreatment from "../inPatientDetail/patientTreatments.jsx";
import IpdPrescriptionModal from "./IpdPrescriptionModal.jsx";
import { useSubscriptionCapabilities } from "../../hooks/useSubscriptionCapabilities";
import { useAuth } from "../../context/AuthContext";
import { filterByOrganization } from "../../utils/organizationUtils";
import {
  APPOINTMENT_AUTO_CANCEL_GRACE_DAYS,
  autoCancelPastAppointments,
  getAppointmentStatusUpper,
  getAppointmentTokenDisplay,
  getPaymentMethodUpper,
  getTokenStatusUpper,
  isPastAutoCancelThreshold,
  isVisibleInDoctorQueue,
} from "../../utils/appointmentCancelUtils";

import {
  QUEUE_TILE_GRID_PROPS,
  formatQueueDateTimeLabels,
  displayQueueId,
} from "../PathManagePatient/components/workflowCardLayout";

/** Match PageShell PATIENTS title / PAGE_TABS_BAR_SX inset. */
const QUEUE_SECTION_SX = { px: 2 };

/** Nested OPD/IPD tab sizing (horizontal inset comes from PAGE_TABS_BAR_SX). */
const QUEUE_NESTED_TABS_SX = {
  minHeight: 32,
  "& .MuiTab-root": {
    minHeight: 32,
    py: 0.25,
  },
};

// Match frontdesk token tile size/layout; no status/token fill colors
const getQueueTilePaperSx = (theme) => ({
  p: 1,
  borderRadius: 2,
  border: "none",
  outline: "none",
  backgroundColor: theme.palette.background.paper,
  height: "100%",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: theme.shadows[6],
    transform: "translateY(-4px)",
  },
});

const QUEUE_TOKEN_CHIP_SX = {
  backgroundColor: "transparent",
  color: "text.primary",
  fontWeight: "bold",
  fontSize: "0.65rem",
  height: "20px",
  px: 0.5,
  border: "none",
  "& .MuiChip-label": { px: 0.5 },
};

const QUEUE_STATUS_CHIP_SX = {
  ml: 0.5,
  backgroundColor: "transparent",
  color: "text.secondary",
  fontWeight: 500,
  fontSize: "0.55rem",
  height: "18px",
  px: 0.5,
  border: "none",
  "& .MuiChip-label": { px: 0.5 },
};

const QUEUE_ACTION_BTN_SX = {
  textTransform: "none",
  fontSize: "0.62rem",
  py: 0.2,
  px: 0.7,
  minHeight: 22,
  lineHeight: 1.2,
  flex: 1,
};

const QueueField = ({ icon, label, value, bold = true }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flex: 1, minWidth: 0 }}>
    {icon}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: "0.55rem", lineHeight: 1 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={bold ? 600 : 400}
        color="text.primary"
        sx={{
          fontSize: "0.7rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          lineHeight: 1.2,
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  </Box>
);

const formatAdmissionDate = (admissionDate) => {
  const { dateLabel } = formatQueueDateTimeLabels(admissionDate);
  return dateLabel;
};

const formatAdmissionTime = (admissionTime, admissionDate) => {
  const { timeLabel } = formatQueueDateTimeLabels(admissionTime, admissionDate);
  return timeLabel;
};

const PatientInQueue = ({ doctorId }) => {
  // console.log("Doctor ID: ", doctorId);
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();

    const { hasCapability, loading: capabilitiesLoading } = useSubscriptionCapabilities();
    const hasIpdCapability = hasCapability("IPD") || hasCapability("In Patient");

    const [open, setOpen] = useState(false);
    const [appoinment_info, setAppointmentInfo] = useState({});
    const [fetching, setFetching] = useState({ today_appointment: true, ipd_upcoming: true });

    // API Integration
    const [appointments, setAppointments] = useState([]);
    const [admissions, setAdmissions] = useState([]);
    const [tab, setTab] = useState("opd");
    // Guards against a slow, stale fetchAppointments/fetchUpcomingAdmissions
    // call (e.g. switching doctors quickly) landing after a newer one and
    // clobbering the UI with the wrong doctor's data.
    const fetchGuardRef = useRef({ appointmentsRequestId: 0, admissionsRequestId: 0, unmounted: false });
    useEffect(() => {
      const guard = fetchGuardRef.current;
      return () => {
        guard.unmounted = true;
      };
    }, []);
    
    // IPD Treatment Modal state
    const [treatmentModalOpen, setTreatmentModalOpen] = useState(false);
    const [selectedAdmission, setSelectedAdmission] = useState(null);
    
    // IPD Prescription Modal state
    const [prescriptionModalOpen, setPrescriptionModalOpen] = useState(false);
    const [selectedPrescriptionAdmission, setSelectedPrescriptionAdmission] = useState(null);
    // OPD appointments don't depend on tab or IPD capability — keeping those out of
    // the deps avoids re-running this 9-call fetch every time the subscription
    // capabilities hook resolves (hasIpdCapability flips false->true) or the user
    // switches tabs.
    useEffect(() => {
        if (doctorId) {
          fetchAppointments();
        }
    }, [doctorId, location.key]);

    useEffect(() => {
      if (doctorId && tab === 'ipd' && hasIpdCapability) {
        fetchUpcomingAdmissions();
      }
    }, [doctorId, tab, hasIpdCapability]);

    // Switch to OPD tab if IPD capability is not available
    useEffect(() => {
      if (!capabilitiesLoading && !hasIpdCapability && tab === 'ipd') {
        setTab('opd');
      }
    }, [hasIpdCapability, capabilitiesLoading, tab]);

    // useEffect(() => {
    //   const timeout = setTimeout(() => {
    //     window.location.reload(); // reloads the entire tab once
    //   }, 5000); // 5 seconds

    //   return () => clearTimeout(timeout); // cleanup on unmount
    // }, []);

    // const fetchTodaysAppointments = async () => {
    //     setFetching({ ...fetching, today_appointment: true });
    //     try {
    //     const appointmentsData =
    //         await appointmentService.getTodaysAppointmentsByDoctor(doctorId);

    //         // Sort appointments by appointmentDateTime in ascending order
    //     const sortedAppointments = appointmentsData.sort((a, b) => 
    //         new Date(a.appointmentDateTime) - new Date(b.appointmentDateTime)
    //     );
    //     setAppointments(sortedAppointments);
            
    //     // setAppointments(appointmentsData);
    //     } catch (error) {
    //     console.error("Error fetching today's appointments:", error);
    //     }
    //     setFetching({ ...fetching, today_appointment: false });
    // };

  const fetchAppointments = async () => {
    const requestId = ++fetchGuardRef.current.appointmentsRequestId;
    const isStale = () => fetchGuardRef.current.unmounted || fetchGuardRef.current.appointmentsRequestId !== requestId;
    setFetching({ ...fetching, today_appointment: true });
    try {
      const today = new Date();

      console.log(`[PatientInQueue] fetchAppointments: doctorId=${doctorId}, today=${format(today, 'yyyy-MM-dd')}`);

      // Fetch today + tomorrow + the next 7 days in parallel instead of one
      // sequential round-trip per day.
      const dayOffsets = Array.from({ length: 9 }, (_, i) => i); // 0=today .. 8 (7 days past tomorrow)
      const dayResults = await Promise.all(
        dayOffsets.map((offset) => {
          const d = new Date(today);
          d.setDate(today.getDate() + offset);
          return appointmentService.getAppointmentsByDoctorAndDate(doctorId, format(d, 'yyyy-MM-dd'));
        })
      );

      const todayAppointments = dayResults[0];
      const tomorrowAppointments = dayResults[1];
      const upcomingAppointments = dayResults.slice(2).flat().filter(Boolean);
      console.log(`[PatientInQueue] Today's appointments: ${todayAppointments?.length || 0}`);
      console.log(`[PatientInQueue] Tomorrow's appointments: ${tomorrowAppointments?.length || 0}`);
      console.log(`[PatientInQueue] Upcoming appointments (2-8 days): ${upcomingAppointments.length}`);
      
      // Combine all appointments
      const allAppointments = [...(todayAppointments || []), ...(tomorrowAppointments || []), ...upcomingAppointments];
      console.log(`[PatientInQueue] Total appointments fetched: ${allAppointments.length}`);
      if (allAppointments.length > 0) {
        console.log(`[PatientInQueue] Sample appointment before filtering:`, allAppointments[0]);
      }
      // Auto-cancel incomplete appointments only after the appointment day has passed
      const now = new Date();
      const gracePeriodDays = APPOINTMENT_AUTO_CANCEL_GRACE_DAYS;
      try {
        const { cancelled } = await autoCancelPastAppointments(allAppointments, {
          graceDays: gracePeriodDays,
          now,
        });
        if (cancelled > 0) {
          console.log(
            `[PatientInQueue] Auto-cancelled ${cancelled} past appointment(s) after ${gracePeriodDays} day(s)`
          );
        }
      } catch (e) {
        console.error("Failed to auto-cancel some no-show appointments", e);
      }

      const shouldHidePastIncomplete = (a) => {
        const tokenStatusUpper = getTokenStatusUpper(a);
        const apptStatusUpper = getAppointmentStatusUpper(a);
        if (tokenStatusUpper === "COMPLETED" || apptStatusUpper === "COMPLETED") {
          return false;
        }
        if (!a.appointmentDateTime) return false;
        return isPastAutoCancelThreshold(a.appointmentDateTime, gracePeriodDays, now);
      };

      // Show ONLY appointments confirmed by front desk (or legacy frontdesk-created with token)
      const filtered = allAppointments.filter((a) => {
        if (!isVisibleInDoctorQueue(a)) return false;

        const isVirtual =
          a?.isVirtual === true ||
          a?.isVirtual === 1 ||
          a?.is_virtual === true ||
          a?.is_virtual === 1 ||
          a?.virtual === true ||
          a?.virtual === 1 ||
          String(a?.appointmentType || "").toUpperCase() === "VIRTUAL" ||
          String(a?.type || "").toUpperCase() === "VIRTUAL";

        if (isVirtual && getPaymentMethodUpper(a) !== "PAID_ONLINE") {
          return false;
        }

        if (a.appointmentDateTime && shouldHidePastIncomplete(a)) {
          return false;
        }

        return true;
      });
      // Debug logging to help troubleshoot filtering issues
      if (filtered.length !== allAppointments.length) {
        console.log(`[PatientInQueue] 📊 Filtered ${allAppointments.length} appointments down to ${filtered.length}`);
        const filteredOut = allAppointments.filter(a => !filtered.includes(a));
        console.log(`[PatientInQueue] Filtered out ${filteredOut.length} appointments. Sample filtered out:`, filteredOut.slice(0, 3));
        filteredOut.slice(0, 3).forEach((apt, idx) => {
          const status = (apt.status || apt.appointmentStatus || apt.appointment_status || "").toUpperCase();
          const isCancel = apt.isCancel === true;
          const tokenNumber = apt.tokenNumber || apt.token_number;
          const token = apt.token;
          const hasToken = (tokenNumber != null && tokenNumber !== "" && String(tokenNumber) !== "0" && tokenNumber !== 0) || 
                          (token != null && token !== "" && String(token) !== "0" && token !== 0);
          console.log(`[PatientInQueue] Filtered out appointment ${idx + 1}: id=${apt.id}, status=${status}, isCancel=${isCancel}, hasToken=${hasToken}, tokenNumber=${tokenNumber}, token=${token}`);
        });
      } else {
        console.log(`[PatientInQueue] ✅ All ${allAppointments.length} appointments passed filtering`);
      }
      
      // Sort appointments by appointmentDateTime in ascending order
      const sortedAppointments = filtered.sort((a, b) => 
        new Date(a.appointmentDateTime) - new Date(b.appointmentDateTime)
      );

      // Sort appointments by token number in ascending order,
        // same sequence as the Frontdesk Token Dashboard.
        // const sortedAppointments = filtered.sort((a, b) => {
        //   const tokenA = Number(getAppointmentTokenDisplay(a) ?? 0);
        //   const tokenB = Number(getAppointmentTokenDisplay(b) ?? 0);

        //   return tokenA - tokenB;
        // });

      console.log(`[PatientInQueue] ✅ Setting ${sortedAppointments.length} sorted appointments`);
      if (isStale()) return;
      setAppointments(sortedAppointments);
    } catch (error) {
      console.error("Error fetching appointments:", error);
    }
    if (isStale()) return;
    setFetching({ ...fetching, today_appointment: false });
  };

  const fetchUpcomingAdmissions = async () => {
    const requestId = ++fetchGuardRef.current.admissionsRequestId;
    const isStale = () => fetchGuardRef.current.unmounted || fetchGuardRef.current.admissionsRequestId !== requestId;
    try {
      setFetching((prev)=>({...prev, ipd_upcoming: true}));

      let all = [];
      
      // Try to use optimized endpoint first (if backend supports it)
      try {
        all = await getAdmissionsByDoctorId(doctorId);
        console.log(`[fetchUpcomingAdmissions] Raw admissions from API:`, all);
        // Filter by organization as defensive measure (backend should already filter via organizationId query param)
        all = filterByOrganization(all || [], user);
        console.log(`[fetchUpcomingAdmissions] After organization filter:`, all);
      } catch (error) {
        // If endpoint doesn't exist yet, fallback to fetching all and filtering
        console.log("Falling back to getAllAdmissions and filtering by doctorId");
        const allAdmissions = await getAllAdmissions();
        // Filter by organization first
        const filteredByOrg = filterByOrganization(allAdmissions || [], user);
        // Then filter by assigned doctor
        all = filteredByOrg.filter((a) => {
          // Filter by assigned doctor - check if doctorId matches
          const assignedDoctor = a.doctorId || a.assignedDoctorId || a.primaryDoctorId;
          return assignedDoctor && Number(assignedDoctor) === Number(doctorId);
        });
      }
      
      const todayStart = new Date();
      todayStart.setHours(0,0,0,0);
      
      // Helper function to parse admission date (handles both string and array formats)
      const parseAdmissionDate = (admissionDate) => {
        if (!admissionDate) return null;
        
        // Handle array format: [year, month, day]
        if (Array.isArray(admissionDate) && admissionDate.length >= 3) {
          return new Date(admissionDate[0], (admissionDate[1]||1)-1, admissionDate[2]||1);
        }
        
        // Handle string format: "2026-01-05" or ISO string
        if (typeof admissionDate === 'string') {
          // Try parsing as ISO date string (YYYY-MM-DD)
          const dateStr = admissionDate.split('T')[0]; // Remove time if present
          const parts = dateStr.split('-');
          if (parts.length === 3) {
            const [year, month, day] = parts.map(Number);
            return new Date(year, month - 1, day);
          }
          // Fallback to Date constructor
          const parsed = new Date(admissionDate);
          if (!isNaN(parsed.getTime())) {
            return parsed;
          }
        }
        
        // Handle Date object
        if (admissionDate instanceof Date) {
          return admissionDate;
        }
        
        return null;
      };
      
      // Filter admissions by status and date
      const statusFiltered = (all || []).filter((a) => {
        const status = String(a.status || "").toLowerCase();
        const matches = status === "admit";
        if (!matches) {
          console.log(`[fetchUpcomingAdmissions] Filtered out admission ${a.id} - status: ${status}`);
        }
        return matches;
      });
      console.log(`[fetchUpcomingAdmissions] After status filter (admit):`, statusFiltered);
      
      const upcoming = statusFiltered
        .filter((a) => {
          const admissionDate = parseAdmissionDate(a.admissionDate);
          if (!admissionDate) {
            console.log(`[fetchUpcomingAdmissions] Filtered out admission ${a.id} - invalid date:`, a.admissionDate);
            return false;
          }
          admissionDate.setHours(0, 0, 0, 0); // Reset time for comparison
          const isUpcoming = admissionDate >= todayStart;
          if (!isUpcoming) {
            console.log(`[fetchUpcomingAdmissions] Filtered out admission ${a.id} - date in past:`, admissionDate);
          }
          return isUpcoming;
        })
        .sort((a,b)=>{
          const da = parseAdmissionDate(a.admissionDate);
          const db = parseAdmissionDate(b.admissionDate);
          if (!da || !db) return 0;
          return da - db;
        });
      
      console.log(`[fetchUpcomingAdmissions] Final upcoming admissions:`, upcoming);
      
      // The admission API already embeds patientName on most rows (same as
      // InPatientsTable's admissions list) — only fall back to a per-patient
      // fetch for the rare row that's missing it, instead of one call per
      // upcoming admission.
      const upcomingWithNames = await Promise.all(
        upcoming.map(async (adm) => {
          if (adm.patientName) {
            return adm;
          }
          try {
            const patient = await getPatientById(adm.patientId);
            return { ...adm, patientName: patient?.name || "N/A" };
          } catch (error) {
            console.error(`Failed to fetch patient ${adm.patientId}:`, error);
            return { ...adm, patientName: "N/A" };
          }
        })
      );
      
      if (isStale()) return;
      setAdmissions(upcomingWithNames);
    } catch (e) {
      console.error("Failed to fetch admissions", e);
      if (!isStale()) setAdmissions([]);
    } finally {
      if (!isStale()) setFetching((prev)=>({...prev, ipd_upcoming: false}));
    }
  };

    const categorizeAppointments = (appointments) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset to start of day for accurate comparison
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const rangeEnd = new Date(today);
    rangeEnd.setDate(today.getDate() + 8); // matches the fetch window in fetchAppointments
    rangeEnd.setHours(23, 59, 59, 999); // End of day

    const categorized = {
      today: [],
      tomorrow: [],
      upcoming: [],
    };

    appointments.forEach(appointment => {
      if (!appointment.appointmentDateTime) {
        console.warn(`Appointment ${appointment.id} has no appointmentDateTime, skipping categorization`);
        return;
      }
      
      try {
        const appointmentDate = new Date(appointment.appointmentDateTime);
        if (isNaN(appointmentDate.getTime())) {
          console.warn(`Invalid appointmentDateTime for appointment ${appointment.id}:`, appointment.appointmentDateTime);
          return;
        }
        
        // Reset time to start of day for date comparison
        const appointmentDateOnly = new Date(appointmentDate);
        appointmentDateOnly.setHours(0, 0, 0, 0);
        const todayOnly = new Date(today);
        todayOnly.setHours(0, 0, 0, 0);
        const tomorrowOnly = new Date(tomorrow);
        tomorrowOnly.setHours(0, 0, 0, 0);
        
        if (appointmentDateOnly.getTime() === todayOnly.getTime()) {
          categorized.today.push(appointment);
        } else if (appointmentDateOnly.getTime() === tomorrowOnly.getTime()) {
          categorized.tomorrow.push(appointment);
        } else if (appointmentDate > tomorrow && appointmentDate <= rangeEnd) {
          categorized.upcoming.push(appointment);
        }
      } catch (error) {
        console.error(`Error categorizing appointment ${appointment.id}:`, error);
      }
    });
    
    return categorized;
  };

  const categorizeAdmissions = (admissions) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const twoWeeksLater = new Date(today);
    twoWeeksLater.setDate(today.getDate() + 14);
    
    const categorized = {
      today: [],
      tomorrow: [],
      upcoming: [],
    };

    // Helper function to parse admission date (handles both string and array formats)
    const parseAdmissionDate = (admissionDate) => {
      if (!admissionDate) return null;
      
      // Handle array format: [year, month, day]
      if (Array.isArray(admissionDate) && admissionDate.length >= 3) {
        return new Date(admissionDate[0], (admissionDate[1]||1)-1, admissionDate[2]||1);
      }
      
      // Handle string format: "2026-01-05" or ISO string
      if (typeof admissionDate === 'string') {
        // Try parsing as ISO date string (YYYY-MM-DD)
        const dateStr = admissionDate.split('T')[0]; // Remove time if present
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          const [year, month, day] = parts.map(Number);
          return new Date(year, month - 1, day);
        }
        // Fallback to Date constructor
        const parsed = new Date(admissionDate);
        if (!isNaN(parsed.getTime())) {
          return parsed;
        }
      }
      
      // Handle Date object
      if (admissionDate instanceof Date) {
        return admissionDate;
      }
      
      return null;
    };

    admissions.forEach(admission => {
      const admissionDate = parseAdmissionDate(admission.admissionDate);
      if (!admissionDate) return;
      
      admissionDate.setHours(0, 0, 0, 0);
      
      if (admissionDate.toDateString() === today.toDateString()) {
        categorized.today.push(admission);
      } else if (admissionDate.toDateString() === tomorrow.toDateString()) {
        categorized.tomorrow.push(admission);
      } else if (admissionDate > tomorrow && admissionDate <= twoWeeksLater) {
        categorized.upcoming.push(admission);
      }
    });
    return categorized;
  };

  const categorizedAppointments = useMemo(
    () => categorizeAppointments(appointments),
    [appointments]
  );
  const categorizedAdmissions = useMemo(
    () => categorizeAdmissions(admissions),
    [admissions]
  );

  // Helper function to extract virtual appointment info
  const getVirtualAppointmentInfo = (appointment) => {
    // Check if appointment is virtual using multiple possible field names
    const isVirtual = appointment?.isVirtual === true || 
                     appointment?.isVirtual === 1 ||
                     appointment?.is_virtual === true ||
                     appointment?.is_virtual === 1 ||
                     appointment?.virtual === true ||
                     appointment?.virtual === 1 ||
                     String(appointment?.appointmentType || "").toUpperCase() === "VIRTUAL" ||
                     String(appointment?.type || "").toUpperCase() === "VIRTUAL";

    // Extract video link from multiple possible locations
    const videoLink = appointment?.virtualMeeting?.url ||
                     appointment?.virtualMeeting?.link ||
                     appointment?.videoLink || 
                     appointment?.meetingLink ||
                     appointment?.virtualLink ||
                     appointment?.videoUrl ||
                     null;

    return { isVirtual, videoLink };
  };

    const UpdateTokenStatus = async (appointmentId) => {
        try {
          console.log(`[PatientInQueue] UpdateTokenStatus called for appointmentId: ${appointmentId}`);
          const updated = await appointmentService.updateTokenStatusToCalled(appointmentId);
          console.log(`[PatientInQueue] UpdateTokenStatus success, updated data:`, updated);
          
          // Refresh only this appointment card optimistically
          setAppointments((prev) => prev.map((a) =>
            a.id === appointmentId ? { ...a, tokenStatus: 'CALLED', tokenNumber: updated?.tokenNumber ?? a.tokenNumber } : a
          ));
        } catch (error) {
          console.error("[PatientInQueue] Error updating token status:", error);
          console.error("[PatientInQueue] Error message:", error.message);
          
          // Show user-friendly error message
          if (error.message && error.message.includes("Appointment not found")) {
            console.error("[PatientInQueue] ⚠️ Appointment not found in organization-specific table. This might be a backend issue.");
            // Optionally show a toast/alert to the user
            // You can uncomment the following if you have toast notifications set up
            // toast.error("Failed to update appointment status. The appointment may not exist in your organization's table.");
          }
          
          // Re-fetch appointments to ensure UI is in sync
          try {
            await fetchAppointments();
          } catch (fetchError) {
            console.error("[PatientInQueue] Error re-fetching appointments after update failure:", fetchError);
          }
        }
    };

    const renderOpdQueueTile = (appointment, { actionsEnabled }) => {
      const { isVirtual, videoLink } = getVirtualAppointmentInfo(appointment);
      const tokenDisplay = getAppointmentTokenDisplay(appointment);
      const tokenLabel = tokenDisplay != null ? `Token #${tokenDisplay}` : "Token #—";
      const { dateLabel, timeLabel } = formatQueueDateTimeLabels(
        appointment.appointmentDateTime,
        appointment.appointment_date_time,
        appointment.appointmentDate,
        appointment.appointmentTime
      );
      const appointmentIdLabel = displayQueueId(
        appointment.id ?? appointment.appointmentId ?? appointment.appointment_id
      );
      return (
        <Grid item {...QUEUE_TILE_GRID_PROPS} key={appointment.id}>
          <Paper elevation={3} sx={getQueueTilePaperSx(theme)}>
            <Stack spacing={0.5}>
              <Box sx={{ textAlign: "center", pb: 0.25 }}>
                <Chip label={tokenLabel} sx={QUEUE_TOKEN_CHIP_SX} />
                {appointment.tokenStatus && (
                  <Chip
                    label={String(appointment.tokenStatus).toUpperCase()}
                    size="small"
                    sx={QUEUE_STATUS_CHIP_SX}
                  />
                )}
                {appointmentIdLabel !== "—" && (
                  <Chip
                    label={`Appt ID #${appointmentIdLabel}`}
                    size="small"
                    sx={{ ...QUEUE_STATUS_CHIP_SX, color: "black" }}
                  />
                )}
              </Box>

              <Divider sx={{ my: 0.25 }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "space-between" }}>
                <QueueField
                  icon={
                    <PersonIcon
                      fontSize="small"
                      color="primary"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  }
                  label="Patient"
                  value={appointment.patientName}
                />
                <QueueField
                  icon={
                    <LocalHospitalIcon
                      fontSize="small"
                      color="primary"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  }
                  label="Doctor"
                  value={appointment.doctorName}
                />
              </Box>

              <Divider sx={{ my: 0.25 }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "space-between" }}>
                <QueueField
                  icon={
                    <CalendarTodayIcon
                      fontSize="small"
                      color="action"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  }
                  label="Date"
                  value={dateLabel}
                  bold={false}
                />
                <QueueField
                  icon={
                    <AccessTimeIcon
                      fontSize="small"
                      color="action"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  }
                  label="Time"
                  value={timeLabel}
                  bold={false}
                />
              </Box>

              {isVirtual && (
                <Box sx={{ pt: 0.25 }}>
                  <Chip
                    icon={<VideoCallIcon sx={{ fontSize: "0.75rem !important" }} />}
                    label="Virtual"
                    color="secondary"
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: "0.55rem", "& .MuiChip-label": { px: 0.5 } }}
                  />
                  {videoLink ? (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<LinkIcon sx={{ fontSize: "0.7rem !important" }} />}
                      endIcon={<OpenInNewIcon sx={{ fontSize: "0.7rem !important" }} />}
                      onClick={() => window.open(videoLink, "_blank")}
                      sx={{ ...QUEUE_ACTION_BTN_SX, mt: 0.5, width: "100%", flex: "none" }}
                    >
                      Join Video Call
                    </Button>
                  ) : (
                    <Alert severity="info" sx={{ py: 0, px: 0.75, mt: 0.5, fontSize: "0.55rem" }}>
                      Video link will be shared before appointment time.
                    </Alert>
                  )}
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 0.5, pt: 0.25 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    setOpen(true);
                    setAppointmentInfo(appointment);
                  }}
                  disabled={!actionsEnabled || appointment.tokenStatus !== "CALLED"}
                  sx={QUEUE_ACTION_BTN_SX}
                >
                  View Details
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  size="small"
                  onClick={() => UpdateTokenStatus(appointment.id)}
                  disabled={!actionsEnabled || appointment.tokenStatus === "CALLED"}
                  sx={QUEUE_ACTION_BTN_SX}
                >
                  Call Patient
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      );
    };

    const renderIpdQueueTile = (adm, { primaryLabel, secondaryLabel, onPrimary, onSecondary }) => (
      <Grid item {...QUEUE_TILE_GRID_PROPS} key={adm.id}>
        <Paper elevation={3} sx={getQueueTilePaperSx(theme)}>
          <Stack spacing={0.5}>
            <Box sx={{ textAlign: "center", pb: 0.25 }}>
              <Chip label={`Admission #${adm.id}`} sx={QUEUE_TOKEN_CHIP_SX} />
              <Chip label="IPD" size="small" sx={QUEUE_STATUS_CHIP_SX} />
              {displayQueueId(adm.appointmentId ?? adm.appointment_id ?? adm.appointment?.id) !== "—" && (
                <Chip
                  label={`Appt ID #${displayQueueId(adm.appointmentId ?? adm.appointment_id ?? adm.appointment?.id)}`}
                  size="small"
                  sx={{ ...QUEUE_STATUS_CHIP_SX, color: "black" }}
                />
              )}
            </Box>

            <Divider sx={{ my: 0.25 }} />

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "space-between" }}>
              <QueueField
                icon={
                  <PersonIcon
                    fontSize="small"
                    color="primary"
                    sx={{ fontSize: "0.7rem" }}
                  />
                }
                label="Patient"
                value={adm.patientName}
              />
              <QueueField
                icon={
                  <LocalHospitalIcon
                    fontSize="small"
                    color="primary"
                    sx={{ fontSize: "0.7rem" }}
                  />
                }
                label="Doctor"
                value={adm.doctorName || "—"}
              />
            </Box>

            <Divider sx={{ my: 0.25 }} />

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "space-between" }}>
              <QueueField
                icon={
                  <CalendarTodayIcon
                    fontSize="small"
                    color="action"
                    sx={{ fontSize: "0.7rem" }}
                  />
                }
                label="Date"
                value={formatAdmissionDate(adm.admissionDate)}
                bold={false}
              />
              <QueueField
                icon={
                  <AccessTimeIcon
                    fontSize="small"
                    color="action"
                    sx={{ fontSize: "0.7rem" }}
                  />
                }
                label="Time"
                value={formatAdmissionTime(
                  adm.admissionTime ?? adm.admission_time,
                  adm.admissionDateTime ?? adm.admissionDate
                )}
                bold={false}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 0.5, pt: 0.25 }}>
              <Button
                variant="contained"
                size="small"
                onClick={onPrimary}
                sx={QUEUE_ACTION_BTN_SX}
              >
                {primaryLabel}
              </Button>
              {secondaryLabel && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={onSecondary}
                  sx={QUEUE_ACTION_BTN_SX}
                >
                  {secondaryLabel}
                </Button>
              )}
            </Box>
          </Stack>
        </Paper>
      </Grid>
    );

    return (
        <Box sx={{ flexGrow: 1, p: 0 }}>
        <Box sx={{ mb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
          <Box sx={{ ...PAGE_TABS_BAR_SX, flex: 1, minWidth: 0 }}>
            <Tabs
              value={tab}
              onChange={(e,v)=>setTab(v)}
              {...PAGE_TAB_LIST_PROPS}
              sx={QUEUE_NESTED_TABS_SX}
            >
              <Tab label="OPD Upcoming" value="opd" />
              {hasIpdCapability && <Tab label="IPD Upcoming" value="ipd" />}
            </Tabs>
          </Box>
          <IconButton 
            onClick={async () => { 
              if (tab === 'opd') {
                await fetchAppointments();
              } else if (tab === 'ipd' && hasIpdCapability) {
                await fetchUpcomingAdmissions();
              }
            }}
          >
            <Refresh />
          </IconButton>
        </Box>

        {tab === 'opd' && (fetching.today_appointment ? (
        <Box display={"flex"} height={"400px"} justifyContent={"center"} alignItems={"center"} width={"100%"}>
          <CircularProgress size={80} />
        </Box>
      ) : (
        <Box sx={QUEUE_SECTION_SX}>
          {categorizedAppointments.today.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.5 }}>Today</Typography>
              <Divider sx={{ mb: 1 }} />
              <Grid container spacing={2}>
                {categorizedAppointments.today.map((appointment) =>
                  renderOpdQueueTile(appointment, { actionsEnabled: true })
                )}
              </Grid>
            </Box>
          )}

            {categorizedAppointments.tomorrow.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.5 }}>Tomorrow</Typography>
              <Divider sx={{ mb: 1 }} />
              <Grid container spacing={2}>
                {categorizedAppointments.tomorrow.map((appointment) =>
                  renderOpdQueueTile(appointment, { actionsEnabled: false })
                )}
              </Grid>
            </Box>
          )}

          {categorizedAppointments.upcoming.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.5 }}>Upcoming Week</Typography>
              <Divider sx={{ mb: 1 }} />
              <Grid container spacing={2}>
                {categorizedAppointments.upcoming.map((appointment) =>
                  renderOpdQueueTile(appointment, { actionsEnabled: false })
                )}
              </Grid>
            </Box>
          )}

          {categorizedAppointments.today.length === 0 && 
           categorizedAppointments.tomorrow.length === 0 && 
           categorizedAppointments.upcoming.length === 0 && (
            <Box display={"flex"} justifyContent={"center"} alignItems={"center"} height={"200px"} marginTop={"20px"} width={"100%"}>
              <Typography variant="body1">No upcoming appointments.</Typography>
            </Box>
          )}
        </Box>
      ))}

      {tab === 'ipd' && hasIpdCapability && (
        fetching.ipd_upcoming ? (
          <Box display={"flex"} height={"400px"} justifyContent={"center"} alignItems={"center"} width={"100%"}>
            <CircularProgress size={80} />
          </Box>
        ) : (
          <Box sx={QUEUE_SECTION_SX}>
            {categorizedAdmissions.today.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.5 }}>Today</Typography>
                <Divider sx={{ mb: 1 }} />
                <Grid container spacing={2}>
                  {categorizedAdmissions.today.map((adm) =>
                    renderIpdQueueTile(adm, {
                      primaryLabel: "View Details",
                      secondaryLabel: null,
                      onPrimary: () => {
                        setSelectedPrescriptionAdmission(adm);
                        setPrescriptionModalOpen(true);
                      },
                    })
                  )}
                </Grid>
              </Box>
            )}

            {categorizedAdmissions.tomorrow.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.5 }}>Tomorrow</Typography>
                <Divider sx={{ mb: 1 }} />
                <Grid container spacing={2}>
                  {categorizedAdmissions.tomorrow.map((adm) =>
                    renderIpdQueueTile(adm, {
                      primaryLabel: "Create Prescription",
                      secondaryLabel: "View Treatment",
                      onPrimary: () => {
                        setSelectedPrescriptionAdmission(adm);
                        setPrescriptionModalOpen(true);
                      },
                      onSecondary: () => {
                        setSelectedAdmission(adm);
                        setTreatmentModalOpen(true);
                      },
                    })
                  )}
                </Grid>
              </Box>
            )}

            {categorizedAdmissions.upcoming.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2, mb: 0.5 }}>Upcoming Week</Typography>
                <Divider sx={{ mb: 1 }} />
                <Grid container spacing={2}>
                  {categorizedAdmissions.upcoming.map((adm) =>
                    renderIpdQueueTile(adm, {
                      primaryLabel: "Create Prescription",
                      secondaryLabel: "View Treatment",
                      onPrimary: () => {
                        setSelectedPrescriptionAdmission(adm);
                        setPrescriptionModalOpen(true);
                      },
                      onSecondary: () => {
                        setSelectedAdmission(adm);
                        setTreatmentModalOpen(true);
                      },
                    })
                  )}
                </Grid>
              </Box>
            )}

            {categorizedAdmissions.today.length === 0 && 
             categorizedAdmissions.tomorrow.length === 0 && 
             categorizedAdmissions.upcoming.length === 0 && (
              <Box display={"flex"} justifyContent={"center"} alignItems={"center"} height={"200px"} marginTop={"20px"} width={"100%"}>
                <Typography variant="body1">No upcoming inpatients.</Typography>
              </Box>
            )}
          </Box>
        )
      )}
        
        <Calendar
            open={open}
            onClose={async () => {
            setOpen(false);
            setAppointmentInfo(null);
            try { await fetchAppointments(); } catch (e) { /* noop */ }
            }}
            appointment={{
            ...(appoinment_info && typeof appoinment_info === "object"
              ? appoinment_info
              : {}),
            patientId:
              appoinment_info?.patientId ??
              appoinment_info?.patient_id ??
              appoinment_info?.patient?.id,
            patientName:
              appoinment_info?.patientName ??
              appoinment_info?.patient_name ??
              appoinment_info?.patient?.name,
            doctorId:
              appoinment_info?.doctorId ??
              appoinment_info?.doctor_id ??
              appoinment_info?.doctor?.id ??
              doctorId,
            doctorName:
              appoinment_info?.doctorName ??
              appoinment_info?.doctor_name ??
              appoinment_info?.doctor?.name,
            id:
              appoinment_info?.id ??
              appoinment_info?.appointmentId ??
              appoinment_info?.appointment_id,
            appointmentId:
              appoinment_info?.appointmentId ??
              appoinment_info?.id ??
              appoinment_info?.appointment_id,
            appointment_id:
              appoinment_info?.appointment_id ??
              appoinment_info?.appointmentId ??
              appoinment_info?.id,
            appointmentDateTime:
              appoinment_info?.appointmentDateTime ??
              appoinment_info?.appointment_date_time,
            appointmentDate:
              appoinment_info?.appointmentDate ??
              appoinment_info?.appointment_date,
            date: appoinment_info?.date,
            }}
        />
        
        <PatientTreatment
          open={treatmentModalOpen}
          onClose={() => {
            setTreatmentModalOpen(false);
            setSelectedAdmission(null);
          }}
          admissionId={selectedAdmission?.id}
          patientId={selectedAdmission?.patientId}
          patient_info={{
            name: selectedAdmission?.patientName,
          }}
        />
        
        <IpdPrescriptionModal
          open={prescriptionModalOpen}
          onClose={() => {
            setPrescriptionModalOpen(false);
            setSelectedPrescriptionAdmission(null);
          }}
          patientId={selectedPrescriptionAdmission?.patientId}
          patientName={selectedPrescriptionAdmission?.patientName}
          admissionId={selectedPrescriptionAdmission?.id}
          admission={selectedPrescriptionAdmission}
          doctorId={doctorId}
        />
        </Box>
    );
};

export default PatientInQueue;
